import { useContainerWidth } from "./flowsChartShared";
import type { StockCapeDto } from "../api";

/**
 * The unified premium-band signal for one STOCK (2026-09-27, extended to
 * 3 tiers 2026-09-28): the stock's own line + ITS OWN historical band
 * (expanding mean +/- 1 sigma) + today's marker. Deliberately a single
 * unsegmented series -- unlike index_cape's mandatory pre/post-2021 split,
 * a per-company earnings basis never changes (verified empirically
 * building app/factory/stock_cape.py: every one of 24,254 earnings_actuals
 * rows carries eps_basis='adjusted', zero mixing). Same point-index x-axis
 * convention as CapeChart.tsx.
 *
 * THREE TIERS, same core question ("above/within/below its own normal
 * band?"), different METRIC and confidence:
 *  - tier1/tier2: the line IS CAPE (price / smoothed trailing EPS).
 *  - tier3: the line is a PREMIUM RATIO (this stock's own P/E / its
 *    sector's median P/E) -- a completely different scale (typically
 *    0.3-3x, not a 10-300x CAPE multiple) for stocks too young for any
 *    CAPE band at all (e.g. recent IPOs). Labelled distinctly below so
 *    the two are never visually confused.
 *
 * THE WHOLE POINT, at every tier: this is RELATIVE valuation. The band IS
 * the stock's own normal premium range, so a growth stock trading at its
 * usual premium reads "fair", not "expensive" -- only a move genuinely
 * outside its own history reads as cheap/expensive. An absolute scale (vs
 * some universal benchmark) is never shown here; it would misleadingly
 * flag every real grower as permanently overvalued.
 */

const HEIGHT = 260;
const MARGIN = { top: 14, right: 12, bottom: 22, left: 44 };
const TICK_COUNT = 8;

function formatTick(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

const CLASS_COLOR: Record<string, string> = {
  cheap: "#1a9d6a", fair: "#b8860b", expensive: "#c6413a",
};

const TIER_LABEL: Record<string, string> = {
  tier1: "10yr+ CAPE band", tier2: "5-10yr CAPE band", tier3: "sector-premium band",
};

export function StockCapeChart({ data }: { data: StockCapeDto }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  if (!data.available || data.points.length === 0) {
    return (
      <div ref={ref} className="empty-state" style={{ minHeight: 100 }}>
        Not enough history for any valuation-band signal{data.reason ? ` -- ${data.reason}` : ""}.
      </div>
    );
  }
  if (width === 0) return <div ref={ref} style={{ minHeight: HEIGHT }} />;

  const isTier3 = data.tier === "tier3";
  const valueName = isTier3 ? "sector-premium ratio" : "CAPE";

  const points = data.points;
  const n = points.length;
  const lastIdx = Math.max(n - 1, 1);
  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;

  const allValues = points.flatMap((p) => [
    p.cape, p.band_mean !== null && p.band_sd !== null ? p.band_mean + p.band_sd : p.cape,
    p.band_mean !== null && p.band_sd !== null ? p.band_mean - p.band_sd : p.cape,
  ]);
  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);
  const yPad = (yMax - yMin) * 0.06 || 1;
  const domainMin = Math.max(yMin - yPad, 0);
  const domainMax = yMax + yPad;

  const xAtIdx = (i: number) => MARGIN.left + (i / lastIdx) * plotW;
  const yAt = (v: number) => MARGIN.top + (1 - (v - domainMin) / (domainMax - domainMin)) * plotH;

  const tickCount = Math.min(TICK_COUNT, n);
  const xTicks = Array.from({ length: tickCount }, (_, i) => {
    const idx = Math.round((i / (tickCount - 1 || 1)) * lastIdx);
    return { idx, date: points[idx].date };
  });

  const withBand = points
    .map((p, i) => ({ p, idx: i }))
    .filter(({ p }) => p.band_mean !== null && p.band_sd !== null);
  const upperPath = withBand.map(({ p, idx }, i) => `${i === 0 ? "M" : "L"}${xAtIdx(idx).toFixed(1)},${yAt(p.band_mean! + p.band_sd!).toFixed(1)}`).join(" ");
  const lowerPath = [...withBand].reverse().map(({ p, idx }) => `L${xAtIdx(idx).toFixed(1)},${yAt(p.band_mean! - p.band_sd!).toFixed(1)}`).join(" ");
  const bandPath = withBand.length > 1 ? `${upperPath} ${lowerPath} Z` : "";
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${xAtIdx(i).toFixed(1)},${yAt(p.cape).toFixed(1)}`).join(" ");

  const todayColor = data.classification ? CLASS_COLOR[data.classification] : "#8a91a0";
  const todayX = xAtIdx(lastIdx);
  const todayY = yAt(points[lastIdx].cape);

  return (
    <div ref={ref}>
      {data.tier && (
        <div className="note" style={{ marginBottom: 4 }}>
          <span className="thin-badge">{TIER_LABEL[data.tier]}</span>
          {data.tier_note && <span style={{ marginLeft: 6 }}>{data.tier_note}</span>}
        </div>
      )}
      <div className="cape-legend">
        <span><i className="cape-swatch line" /> {isTier3 ? "premium to sector (this stock's P/E ÷ sector median P/E)" : "CAPE (nominal, smoothed-earnings)"}</span>
        <span><i className="cape-swatch band" /> this stock's own historical range (expanding mean ± 1σ)</span>
      </div>
      <svg width={width} height={HEIGHT} role="img" aria-label="Relative valuation-band chart">
        {bandPath && <path d={bandPath} fill="#8a91a033" stroke="none" />}
        <path d={linePath} fill="none" stroke="#1a9d8a" strokeWidth={1.6} />
        <circle cx={todayX} cy={todayY} r={4} fill={todayColor} stroke="#fff" strokeWidth={1.2} />

        {xTicks.map(({ idx, date }, i) => (
          <text key={i} x={xAtIdx(idx)} y={HEIGHT - 6} fontSize={9} fill="#8a91a0" textAnchor="middle">
            {formatTick(date)}
          </text>
        ))}
        <text x={4} y={yAt(domainMax) + 8} fontSize={9} fill="#8a91a0">{domainMax.toFixed(isTier3 ? 2 : 0)}</text>
        <text x={4} y={yAt(domainMin) + 2} fontSize={9} fill="#8a91a0">{domainMin.toFixed(isTier3 ? 2 : 0)}</text>
      </svg>
      {data.classification && (
        <div style={{ fontSize: "0.78rem", marginTop: 2 }}>
          <span style={{ color: todayColor, fontWeight: 700 }}>●</span> today ({data.series_end}):{" "}
          {valueName} {data.current_cape!.toFixed(isTier3 ? 2 : 1)}× —{" "}
          <span style={{ color: todayColor, fontWeight: 600 }}>
            {data.classification.toUpperCase()}
          </span>{" "}
          vs this stock's own {isTier3 ? "sector-premium" : "CAPE"} history (mean{" "}
          {data.band_mean!.toFixed(isTier3 ? 2 : 1)}× ± {data.band_sd!.toFixed(isTier3 ? 2 : 1)})
        </div>
      )}
      {data.band_stability?.status === "trending" && data.band_stability.note && (
        <div className="note" style={{ marginTop: 6, color: "#b8860b" }}>
          ⚠ {data.band_stability.note}
        </div>
      )}
      {data.persistent_breakout && (
        <div className="note" style={{ marginTop: 6, color: data.persistent_breakout.direction === "above" ? "#c6413a" : "#1a9d6a" }}>
          ⚠ {data.persistent_breakout.note}
        </div>
      )}
    </div>
  );
}
